import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Ripple } from "primereact/ripple";
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import NoDataCreated from "../../../../../Components/NoDataCreated";
import ErrorPage from "../../../../../Components/ErrorPage";
import PageLoader from "../../../../../Components/PageLoader";
import { goToTop } from "../../../../../Components/goToTop";
import AlertModal from "../../../../../Components/AlertModal";
import { getLoginUserRoleName, UserRoleName } from "../../../../../Utils/LoginUserRoleName";
import { useAppDispatch, useAppSelector } from "../../../../../Controllers/hooks";
import { getAllPlanChckList } from "../../../../../Controllers/Thunks/PlanCheckThunks";
import { PlanCheck } from "../../../../../Controllers/Types/PlanCheck/PlanCheckType";

const PlanCheckList: React.FC = () => {
    const toast = useRef<Toast>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const projectData = location.state?.data;
    const serviceId = Number(projectData.folder_id);
    const [showAlertModal, setShowAlertModal] = useState<boolean>(false);
    const [alertMessage, setAlertMessage] = useState<string>('');

    const loadingPage = useAppSelector((state) => state?.planCheckList?.loader);
    const error = useAppSelector((state) => state?.planCheckList?.errorDisplay);
    const [loginUserRoleName, setLoginUserRoleName] = useState<UserRoleName | ''>('');

    const [planCheckData, setPlanCheckData] = useState<PlanCheck[]>([]);

    const dispatch = useAppDispatch();

    // for fetching data fro api tigger with state object actions
    useEffect(() => {
        dispatch(getAllPlanChckList(serviceId));
    }, [location.state]);

    // this is get data from state list and set projectlist
    const plancheckList = useAppSelector((state) => state.planCheckList.planCheckList);

    useEffect(() => {
        const haveData = plancheckList && plancheckList.length > 0;
        if (haveData) {
            if (loginUserRoleName === 'building inspector') {
                const validPlanCheck = plancheckList?.filter((data) => (data?.stage_for === 'Original' || (data?.is_sign_report_by_arc === 1 && data?.is_published_by_arc === 1)))
                setPlanCheckData(validPlanCheck);
            } else {
                setPlanCheckData(plancheckList);
            }
        }
    }, [plancheckList, loginUserRoleName]);

    useEffect(() => {
        const fetchUserRole = async () => {
            const roleName = await getLoginUserRoleName();
            setLoginUserRoleName(roleName);
        };

        fetchUserRole();
    }, []);

    /* **************************** plan check list ****************************** */
    const getRowClassName = (rowData: PlanCheck, options: { [key: string]: any }) => {
        const stage = rowData?.stage_for;

        if (stage === 'Original') {
            return "project_data_row primary";
        } else {
            return "project_data_row secondary";
        }
    };

    const handleCreatePlanCheck = (data: PlanCheck) => {
        const isPublishedByBI = data?.is_published_by_bi;
        const planCheckType = data?.check_type;

        if (loginUserRoleName === 'architect' && isPublishedByBI === 0) {
            setShowAlertModal(true);
            setAlertMessage(`That document has not yet been prepared and published by the building inspector.`)
        } else {
            if (planCheckType === 'Detail') {
                navigate(`/project/detail/default-service/pre-commencement/plan-check/detail`, { state: { data: data, projectData: projectData } });
            } else {
                navigate(`/project/detail/default-service/pre-commencement/plan-check/basic`, { state: { data: data, projectData: projectData } });
            }
        }

        goToTop();
    }

    const handleViewPlanCheck = (data: PlanCheck) => {
        const isSignedAndPublishedByBI = data?.is_published_by_bi === 1 && data?.is_sign_report_by_bi === 1;
        const planCheckType = data?.check_type;
        const stageFor = data?.stage_for;

        if (loginUserRoleName === 'architect' && isSignedAndPublishedByBI === false && stageFor === 'Original') {
            setShowAlertModal(true);
            setAlertMessage(`That document has not yet been signed & published by the building inspector.`)
        } else {
            if (planCheckType === 'Detail') {
                navigate(`/project/detail/default-service/pre-commencement/plan-check/detail`, { state: { data: data, projectData: projectData } });
            } else {
                navigate(`/project/detail/default-service/pre-commencement/plan-check/basic`, { state: { data: data, projectData: projectData } });
            }
        }

        goToTop();
    }

    const statusBodyTemplate = (rowData: PlanCheck) => {
        const isSignedAndPublishedByBi = rowData?.is_sign_report_by_bi === 1 && rowData?.is_published_by_bi === 1;
        const isSignedAndPublishedByArc = rowData?.is_sign_report_by_arc === 1 && rowData?.is_published_by_arc === 1;
        const stage = rowData?.stage_for;
        let status = "";
        let colorCode = "";

        if (loginUserRoleName === 'architect') {
            if ((isSignedAndPublishedByArc && stage === 'Response') || (isSignedAndPublishedByBi && stage === 'Original')) {
                status = 'Published';
            } else {
                status = 'Draft';
            }
        } else if (loginUserRoleName === 'building inspector') {
            if ((isSignedAndPublishedByBi && stage === 'Original') || (isSignedAndPublishedByArc && stage === 'Response')) {
                status = 'Published';
            } else {
                status = 'Draft';
            }
        }

        if (status === "Published") {
            colorCode = "#B9F6CA";
        } else if (status === "Draft") {
            colorCode = "#FFEFBC";
        }

        return (
            <div className="flex_center">
                <span className="priority_label" style={{ backgroundColor: colorCode }}>
                    {status}
                </span>
            </div>
        );
    };

    const stageBodyTemplate = (rowData: PlanCheck) => {
        const stage = rowData?.stage_for;
        let stageLabel: 'My Response' | 'Building Inspector Response' | 'Architect Response' | '' = "";

        if (loginUserRoleName === 'architect') {
            if (stage === 'Response') {
                stageLabel = 'My Response';
            } else {
                stageLabel = 'Building Inspector Response';
            }
        } else if (loginUserRoleName === 'building inspector') {
            if (stage === 'Original') {
                stageLabel = 'My Response';
            } else {
                stageLabel = 'Architect Response';
            }
        }

        return (
            <div>
                <span className="text_bold">
                    {rowData?.stage ? rowData?.stage : '--'}
                </span>

                <span className={`draft_lable mt-1 ${stageLabel === 'My Response' ? 'text_purple' : 'text-secondary'}`}>
                    {stageLabel}
                </span>
            </div>
        )
    }

    const viewPlanCheckBody = (rowData: PlanCheck) => {
        const data = rowData;
        const planCheckType = data?.check_type;
        const planCheckPartData = data?.plan_check_part;
        const planCheckItemData = data?.item_list;

        return (
            <div className="tabel_btn_area gap-2 mt-2 mb-2">
                {loginUserRoleName === 'architect' ? (
                    <button
                        type="button"
                        className="tabel_btn fixed_width p-ripple"
                        onClick={() => handleViewPlanCheck(rowData)}
                    >
                        <i className="bi bi-eye me-2"></i>
                        View
                        <Ripple />
                    </button>
                ) : loginUserRoleName === 'building inspector' ? (
                    <React.Fragment>
                        {(planCheckType === 'Basic' && planCheckItemData?.length === 0) || (planCheckType === 'Detail' && planCheckPartData?.length === 0) ? (
                            <button
                                type="button"
                                className="tabel_btn fixed_width p-ripple"
                                onClick={() => handleCreatePlanCheck(rowData)}
                            >
                                <i className="bi bi-plus-circle me-2"></i>
                                Create
                                <Ripple />
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="tabel_btn fixed_width p-ripple"
                                onClick={() => handleViewPlanCheck(rowData)}
                            >
                                <i className="bi bi-eye me-2"></i>
                                View
                                <Ripple />
                            </button>
                        )}
                    </React.Fragment>
                ) : null}
            </div>
        )
    }
    /* *************************************************************************** */


    /* **************************** Common functions ***************************** */
    const goBack = () => {
        navigate(-2);
        goToTop();
    };
    /* *************************************************************************** */

    return (
        <React.Fragment>
            <Toast ref={toast} />

            <div className="main_content layout_padding show_side_bar">
                {loadingPage ? (
                    <PageLoader />
                ) : error ? (
                    <ErrorPage errorMessage={error} />
                ) : (
                    <div className="page_content mt-0">
                        <div className="page_breadcrumb_area">
                            <ul className="page_breadcrumb">
                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-2)}>
                                    {projectData.job_number} : {projectData.address}
                                </li>
                                <li className="page_breadcrumb_item has_child">{projectData.folder}</li>
                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-1)}>
                                    {projectData.feature_name}
                                </li>
                                <li className="page_breadcrumb_item active">Plan check list</li>
                            </ul>

                            <button className="back_btn p-ripple" onClick={goBack}>
                                <i className="bi bi-arrow-left me-2"></i>
                                Back
                                <Ripple />
                            </button>
                        </div>

                        <div className="page_main_content_section">
                            {planCheckData && planCheckData?.length > 0 ? (
                                <DataTable
                                    value={planCheckData}
                                    paginator
                                    size="small"
                                    rows={10}
                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                    tableStyle={{ minWidth: '50rem' }}
                                    paginatorDropdownAppendTo={'self'}
                                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                                    currentPageReportTemplate="{first} to {last} of {totalRecords}"
                                    className="page_table p-0 p-sm-2"
                                    rowHover
                                    rowClassName={getRowClassName}
                                >
                                    <Column
                                        header="Stage"
                                        headerClassName="text_purple"
                                        body={stageBodyTemplate}
                                        className="py-3"
                                        style={{ width: '20%' }}
                                    ></Column>

                                    <Column
                                        header="Status"
                                        headerClassName="text_purple"
                                        alignHeader={"center"}
                                        body={statusBodyTemplate}
                                        style={{ width: '20%' }}
                                    ></Column>

                                    <Column
                                        header="Issued Date"
                                        headerClassName="text_purple"
                                        body={(rowData: PlanCheck) => (
                                            <span className="text_no_wrap">
                                                {rowData?.date ? rowData?.date : '--'}
                                            </span>
                                        )}
                                        style={{ width: '40%' }}
                                    ></Column>

                                    <Column
                                        alignHeader="center"
                                        body={viewPlanCheckBody}
                                        style={{ width: '20%' }}
                                    ></Column>
                                </DataTable>
                            ) : (
                                <NoDataCreated
                                    message={`The plan check has not been issued yet.!`}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Alert modal */}
            <AlertModal
                isOpen={showAlertModal}
                onClose={() => setShowAlertModal(false)}
                message={alertMessage}
            />
        </React.Fragment>
    )
}

export default PlanCheckList;